import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

type Props = {
  page: number
  total: number
  limit: number
  onPageChange: (page: number) => void
  className?: string
}

const range = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i)

const buildPages = (current: number, totalPages: number): (number | "…")[] => {
  if (totalPages <= 7) return range(1, totalPages)
  const left = Math.max(2, current - 1)
  const right = Math.min(totalPages - 1, current + 1)
  const pages: (number | "…")[] = [1]
  if (left > 2) pages.push("…")
  pages.push(...range(left, right))
  if (right < totalPages - 1) pages.push("…")
  pages.push(totalPages)
  return pages
}

export const Pagination = ({ page, total, limit, onPageChange, className }: Props) => {
  const totalPages = Math.max(1, Math.ceil(total / limit))
  if (totalPages <= 1) return null

  const pages = buildPages(page, totalPages)

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <Button
        variant="ghost"
        size="icon"
        className="size-8"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
      >
        <ChevronLeft className="size-4" />
      </Button>

      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`ellipsis-${i}`} className="px-1 text-sm text-muted-foreground">
            …
          </span>
        ) : (
          <Button
            key={p}
            variant="ghost"
            size="icon"
            className={cn(
              "size-8 text-sm",
              p === page &&
                "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
            )}
            onClick={() => onPageChange(p)}
            aria-current={p === page ? "page" : undefined}
          >
            {p}
          </Button>
        ),
      )}

      <Button
        variant="ghost"
        size="icon"
        className="size-8"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        aria-label="Next page"
      >
        <ChevronRight className="size-4" />
      </Button>
    </div>
  )
}
